package com.mgacreative.hotelru.feature.admin.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material.icons.outlined.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.mgacreative.hotelru.domain.model.PricePeriod
import com.mgacreative.hotelru.shared.ui.designsystem.components.LanguageSelectorRow
import com.mgacreative.hotelru.shared.ui.designsystem.components.VisualDatePicker
import com.mgacreative.hotelru.shared.ui.designsystem.components.toDisplayDate
import com.mgacreative.hotelru.shared.ui.designsystem.components.verticalScrollbar
import com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n
import kotlinx.datetime.Clock
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PricingManagementScreen(onNavigateBack: () -> Unit = {}) {
    val viewModel = koinViewModel<AdminPricingViewModel>()
    val periods by viewModel.pricePeriods.collectAsState()
    val roomTypes = viewModel.roomTypes

    var showAddDialog by remember { mutableStateOf(false) }

    // Edit/Delete Dialog states
    var selectedPeriodForEdit by remember { mutableStateOf<PricePeriod?>(null) }
    var editPeriodName by remember { mutableStateOf("") }
    var editPeriodStart by remember { mutableStateOf("") }
    var editPeriodEnd by remember { mutableStateOf("") }
    var editPeriodPrice by remember { mutableStateOf("") }
    var showEditStartDatePicker by remember { mutableStateOf(false) }
    var showEditEndDatePicker by remember { mutableStateOf(false) }

    // Bulk Delete Confirmation Dialog states
    var showBulkDeleteConfirm by remember { mutableStateOf<List<PricePeriod>?>(null) }

    val listState = rememberLazyListState()

    LaunchedEffect(Unit) {
        viewModel.loadData()
    }

    // Group periods by name, start, and end
    val groupedPeriods = remember(periods) {
        periods.groupBy { "${it.name}_${it.startDate}_${it.endDate}" }
    }

    Scaffold(
        containerColor = Color(0xFFF8FAFC),
        topBar = {
            Surface(
                color = Color.White,
                shadowElevation = 1.dp
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 12.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        IconButton(onClick = onNavigateBack) {
                            Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = l10n("Geri"), tint = Color(0xFF0F172A))
                        }
                        Spacer(Modifier.width(8.dp))
                        Column {
                            Text(
                                text = l10n("Fiyat Periyotları & Tarifeler"),
                                fontSize = 18.sp,
                                fontWeight = FontWeight.Bold,
                                color = Color(0xFF0F172A)
                            )
                            Text(
                                text = "${groupedPeriods.size} ${l10n("Dönem Tanımlı")} • ₽ (RUB)",
                                fontSize = 12.sp,
                                color = Color(0xFF64748B)
                            )
                        }
                    }

                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Button(
                            onClick = { showAddDialog = true },
                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                            shape = RoundedCornerShape(10.dp),
                            contentPadding = PaddingValues(horizontal = 14.dp, vertical = 8.dp)
                        ) {
                            Icon(Icons.Default.Add, contentDescription = null, tint = Color.White, modifier = Modifier.size(18.dp))
                            Spacer(Modifier.width(6.dp))
                            Text(l10n("Yeni Periyot Ekle"), fontSize = 13.sp, fontWeight = FontWeight.SemiBold, color = Color.White)
                        }

                        LanguageSelectorRow(isDarkTheme = false)
                    }
                }
            }
        }
    ) { padding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .verticalScrollbar(listState)
        ) {
            if (groupedPeriods.isEmpty()) {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center
                ) {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        modifier = Modifier.padding(24.dp)
                    ) {
                        Box(
                            modifier = Modifier
                                .size(64.dp)
                                .clip(CircleShape)
                                .background(Color(0xFFE2E8F0)),
                            contentAlignment = Alignment.Center
                        ) {
                            Icon(Icons.Outlined.PriceChange, contentDescription = null, tint = Color(0xFF64748B), modifier = Modifier.size(32.dp))
                        }
                        Spacer(Modifier.height(16.dp))
                        Text(l10n("Henüz Fiyat Periyodu Eklenmedi"), fontSize = 16.sp, fontWeight = FontWeight.Bold, color = Color(0xFF0F172A))
                        Spacer(Modifier.height(6.dp))
                        Text(l10n("Sağ üstteki 'Yeni Periyot Ekle' butonundan sezonluk oda tarifelerinizi tanımlayabilirsiniz."), fontSize = 13.sp, color = Color(0xFF64748B))
                    }
                }
            } else {
                LazyColumn(
                    state = listState,
                    modifier = Modifier.fillMaxSize(),
                    contentPadding = PaddingValues(16.dp),
                    verticalArrangement = Arrangement.spacedBy(14.dp)
                ) {
                    items(items = groupedPeriods.values.toList()) { group: List<PricePeriod> ->
                        val firstPeriod = group.firstOrNull()
                        if (firstPeriod != null) {
                            var expanded by remember { mutableStateOf(true) }

                            Card(
                                shape = RoundedCornerShape(18.dp),
                                colors = CardDefaults.cardColors(containerColor = Color.White),
                                elevation = CardDefaults.cardElevation(defaultElevation = 0.dp),
                                border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                Column(modifier = Modifier.padding(16.dp)) {
                                    Row(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .clickable { expanded = !expanded },
                                        horizontalArrangement = Arrangement.SpaceBetween,
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Row(
                                            verticalAlignment = Alignment.CenterVertically,
                                            modifier = Modifier.weight(1f)
                                        ) {
                                            Box(
                                                modifier = Modifier
                                                    .size(42.dp)
                                                    .clip(RoundedCornerShape(10.dp))
                                                    .background(Color(0xFFFEF3C7)),
                                                contentAlignment = Alignment.Center
                                            ) {
                                                Icon(
                                                    imageVector = Icons.Outlined.DateRange,
                                                    contentDescription = null,
                                                    tint = Color(0xFFD97706),
                                                    modifier = Modifier.size(22.dp)
                                                )
                                            }

                                            Spacer(Modifier.width(12.dp))

                                            Column {
                                                Text(
                                                    text = firstPeriod.name,
                                                    fontSize = 15.sp,
                                                    fontWeight = FontWeight.Bold,
                                                    color = Color(0xFF0F172A)
                                                )
                                                Spacer(Modifier.height(2.dp))
                                                Text(
                                                    text = "${firstPeriod.startDate.toDisplayDate()}  ➔  ${firstPeriod.endDate.toDisplayDate()}",
                                                    fontSize = 12.sp,
                                                    color = Color(0xFF64748B),
                                                    fontWeight = FontWeight.Medium
                                                )
                                            }
                                        }

                                        Row(verticalAlignment = Alignment.CenterVertically) {
                                            IconButton(
                                                onClick = { showBulkDeleteConfirm = group },
                                                modifier = Modifier.size(36.dp)
                                            ) {
                                                Icon(
                                                    Icons.Outlined.Delete,
                                                    contentDescription = l10n("Periyodu Sil"),
                                                    tint = Color(0xFFDC2626),
                                                    modifier = Modifier.size(18.dp)
                                                )
                                            }
                                            Spacer(Modifier.width(4.dp))
                                            Icon(
                                                if (expanded) Icons.Default.KeyboardArrowUp else Icons.Default.KeyboardArrowDown,
                                                contentDescription = null,
                                                tint = Color(0xFF64748B)
                                            )
                                        }
                                    }

                                    if (expanded) {
                                        Spacer(Modifier.height(12.dp))
                                        HorizontalDivider(color = Color(0xFFF1F5F9))
                                        Spacer(Modifier.height(10.dp))

                                        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                                            group.forEach { period ->
                                                Surface(
                                                    shape = RoundedCornerShape(10.dp),
                                                    color = Color(0xFFF8FAFC),
                                                    border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
                                                    modifier = Modifier
                                                        .fillMaxWidth()
                                                        .clickable {
                                                            selectedPeriodForEdit = period
                                                            editPeriodName = period.name
                                                            editPeriodStart = period.startDate
                                                            editPeriodEnd = period.endDate
                                                            editPeriodPrice = period.price.toString()
                                                        }
                                                ) {
                                                    Row(
                                                        modifier = Modifier
                                                            .fillMaxWidth()
                                                            .padding(horizontal = 14.dp, vertical = 10.dp),
                                                        horizontalArrangement = Arrangement.SpaceBetween,
                                                        verticalAlignment = Alignment.CenterVertically
                                                    ) {
                                                        Text(
                                                            text = l10n(period.roomType),
                                                            fontSize = 13.sp,
                                                            fontWeight = FontWeight.Medium,
                                                            color = Color(0xFF334155)
                                                        )
                                                        Row(
                                                            verticalAlignment = Alignment.CenterVertically,
                                                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                                                        ) {
                                                            Text(
                                                                text = "${period.price} ₽",
                                                                fontSize = 14.sp,
                                                                fontWeight = FontWeight.Bold,
                                                                color = Color(0xFF0F172A)
                                                            )
                                                            Icon(
                                                                imageVector = Icons.Outlined.Edit,
                                                                contentDescription = null,
                                                                tint = Color(0xFF94A3B8),
                                                                modifier = Modifier.size(16.dp)
                                                            )
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }

                    item {
                        Spacer(Modifier.height(32.dp))
                    }
                }
            }
        }
    }

    // Add Bulk Price Period Dialog
    if (showAddDialog) {
        AddBulkPricingDialog(
            roomTypes = roomTypes,
            onDismiss = { showAddDialog = false },
            onAdd = { name, start, end, pricesMap ->
                viewModel.addBulkPricePeriods(name, start, end, pricesMap)
                showAddDialog = false
            }
        )
    }

    // Bulk Delete Confirmation
    if (showBulkDeleteConfirm != null) {
        AlertDialog(
            onDismissRequest = { showBulkDeleteConfirm = null },
            containerColor = Color.White,
            shape = RoundedCornerShape(20.dp),
            title = { Text(l10n("Periyodu Sil"), fontWeight = FontWeight.Bold) },
            text = { Text(l10n("Bu periyottaki tüm oda fiyatları kalıcı olarak silinecek. Onaylıyor musunuz?")) },
            confirmButton = {
                Button(
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFDC2626)),
                    shape = RoundedCornerShape(8.dp),
                    onClick = {
                        val ids = showBulkDeleteConfirm!!.map { it.id }
                        viewModel.deletePricePeriods(ids)
                        showBulkDeleteConfirm = null
                    }
                ) {
                    Text(l10n("Toplu Sil"), color = Color.White, fontWeight = FontWeight.Bold)
                }
            },
            dismissButton = {
                TextButton(onClick = { showBulkDeleteConfirm = null }) {
                    Text(l10n("İptal"), color = Color(0xFF64748B))
                }
            }
        )
    }

    // Edit Dialog for individual room price
    if (selectedPeriodForEdit != null) {
        AlertDialog(
            onDismissRequest = { selectedPeriodForEdit = null },
            containerColor = Color.White,
            shape = RoundedCornerShape(20.dp),
            title = { Text("${l10n(selectedPeriodForEdit?.roomType ?: "")} ${l10n("Fiyatını Düzenle")}", fontWeight = FontWeight.Bold) },
            text = {
                Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    OutlinedTextField(
                        value = editPeriodName,
                        onValueChange = { editPeriodName = it },
                        label = { Text(l10n("Periyot Adı")) },
                        shape = RoundedCornerShape(10.dp),
                        modifier = Modifier.fillMaxWidth()
                    )
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Box(modifier = Modifier.weight(1f)) {
                            OutlinedTextField(
                                value = if (editPeriodStart.isNotBlank()) editPeriodStart.toDisplayDate() else "",
                                onValueChange = {},
                                readOnly = true,
                                label = { Text(l10n("Başlangıç")) },
                                shape = RoundedCornerShape(10.dp),
                                modifier = Modifier.fillMaxWidth()
                            )
                            Box(modifier = Modifier.matchParentSize().clickable { showEditStartDatePicker = true })
                        }
                        Box(modifier = Modifier.weight(1f)) {
                            OutlinedTextField(
                                value = if (editPeriodEnd.isNotBlank()) editPeriodEnd.toDisplayDate() else "",
                                onValueChange = {},
                                readOnly = true,
                                label = { Text(l10n("Bitiş")) },
                                shape = RoundedCornerShape(10.dp),
                                modifier = Modifier.fillMaxWidth()
                            )
                            Box(modifier = Modifier.matchParentSize().clickable { showEditEndDatePicker = true })
                        }
                    }
                    OutlinedTextField(
                        value = editPeriodPrice,
                        onValueChange = { editPeriodPrice = it },
                        label = { Text("${l10n("Fiyat")} (₽)") },
                        shape = RoundedCornerShape(10.dp),
                        modifier = Modifier.fillMaxWidth()
                    )
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        val price = editPeriodPrice.toDoubleOrNull()
                        if (editPeriodName.isNotBlank() && editPeriodStart.isNotBlank() && editPeriodEnd.isNotBlank() && price != null) {
                            selectedPeriodForEdit?.let { period ->
                                viewModel.updatePricePeriod(period.id, editPeriodName, editPeriodStart, editPeriodEnd, price)
                            }
                            selectedPeriodForEdit = null
                        }
                    },
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                    shape = RoundedCornerShape(8.dp)
                ) {
                    Text(l10n("Güncelle"), fontWeight = FontWeight.Bold, color = Color.White)
                }
            },
            dismissButton = {
                TextButton(
                    onClick = {
                        selectedPeriodForEdit?.let { period ->
                            viewModel.deletePricePeriod(period.id)
                        }
                        selectedPeriodForEdit = null
                    }
                ) {
                    Text(l10n("Sil"), color = Color(0xFFDC2626))
                }
            }
        )
    }

    if (showEditStartDatePicker) {
        val initial = try { LocalDate.parse(editPeriodStart) } catch (e: Exception) { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date }
        VisualDatePicker(
            initialDate = initial,
            onDismissRequest = { showEditStartDatePicker = false },
            onDateSelected = { date ->
                editPeriodStart = date.toString()
                showEditStartDatePicker = false
            }
        )
    }

    if (showEditEndDatePicker) {
        val initial = try { LocalDate.parse(editPeriodEnd) } catch (e: Exception) { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date }
        VisualDatePicker(
            initialDate = initial,
            onDismissRequest = { showEditEndDatePicker = false },
            onDateSelected = { date ->
                editPeriodEnd = date.toString()
                showEditEndDatePicker = false
            }
        )
    }
}

@Composable
private fun AddBulkPricingDialog(
    roomTypes: List<String>,
    onDismiss: () -> Unit,
    onAdd: (String, String, String, Map<String, Double>) -> Unit
) {
    var periodName by remember { mutableStateOf("") }
    var periodStart by remember { mutableStateOf("") }
    var periodEnd by remember { mutableStateOf("") }
    val roomPrices = remember { mutableStateMapOf<String, String>() }

    var showStartDatePicker by remember { mutableStateOf(false) }
    var showEndDatePicker by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(roomTypes) {
        roomTypes.forEach { roomPrices[it] = "" }
    }

    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = Color.White,
        shape = RoundedCornerShape(20.dp),
        title = {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(Icons.Outlined.PriceChange, contentDescription = null, tint = Color(0xFF2563EB))
                Spacer(Modifier.width(8.dp))
                Text(l10n("Yeni Fiyat Periyodu Ekle"), fontWeight = FontWeight.Bold, fontSize = 18.sp)
            }
        },
        text = {
            Column(
                modifier = Modifier.verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                OutlinedTextField(
                    value = periodName,
                    onValueChange = {
                        periodName = it
                        errorMessage = null
                    },
                    label = { Text(l10n("Periyot Adı (Örn: Yaz Sezonu)")) },
                    singleLine = true,
                    shape = RoundedCornerShape(10.dp),
                    modifier = Modifier.fillMaxWidth()
                )

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Box(modifier = Modifier.weight(1f)) {
                        OutlinedTextField(
                            value = if (periodStart.isNotBlank()) periodStart.toDisplayDate() else "",
                            onValueChange = {},
                            readOnly = true,
                            label = { Text(l10n("Başlangıç")) },
                            shape = RoundedCornerShape(10.dp),
                            modifier = Modifier.fillMaxWidth()
                        )
                        Box(modifier = Modifier.matchParentSize().clickable { showStartDatePicker = true })
                    }
                    Box(modifier = Modifier.weight(1f)) {
                        OutlinedTextField(
                            value = if (periodEnd.isNotBlank()) periodEnd.toDisplayDate() else "",
                            onValueChange = {},
                            readOnly = true,
                            label = { Text(l10n("Bitiş")) },
                            shape = RoundedCornerShape(10.dp),
                            modifier = Modifier.fillMaxWidth()
                        )
                        Box(modifier = Modifier.matchParentSize().clickable { showEndDatePicker = true })
                    }
                }

                Text(
                    text = l10n("Oda Tipi Fiyatları (₽)"),
                    fontSize = 13.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color(0xFF0F172A)
                )

                roomTypes.forEach { type ->
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Text(
                            text = l10n(type),
                            fontSize = 13.sp,
                            color = Color(0xFF334155),
                            modifier = Modifier.weight(1.2f)
                        )
                        OutlinedTextField(
                            value = roomPrices[type] ?: "",
                            onValueChange = { roomPrices[type] = it },
                            placeholder = { Text("0.0 ₽", fontSize = 12.sp) },
                            singleLine = true,
                            shape = RoundedCornerShape(8.dp),
                            modifier = Modifier.weight(1f)
                        )
                    }
                }

                if (errorMessage != null) {
                    Text(errorMessage ?: "", color = Color(0xFFDC2626), fontSize = 12.sp)
                }
            }
        },
        confirmButton = {
            val emptyFormError = l10n("Lütfen periyot adı, tarihler ve en az bir oda fiyatı giriniz.")
            Button(
                onClick = {
                    val pricesMap = mutableMapOf<String, Double>()
                    roomPrices.forEach { (type, priceStr) ->
                        priceStr.replace(",", ".").toDoubleOrNull()?.let { pricesMap[type] = it }
                    }

                    if (periodName.isBlank() || periodStart.isBlank() || periodEnd.isBlank() || pricesMap.isEmpty()) {
                        errorMessage = emptyFormError
                        return@Button
                    }

                    onAdd(periodName.trim(), periodStart, periodEnd, pricesMap)
                },
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                shape = RoundedCornerShape(8.dp)
            ) {
                Text(l10n("Kaydet"), fontWeight = FontWeight.Bold, color = Color.White)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(l10n("İptal"), color = Color(0xFF64748B))
            }
        }
    )

    if (showStartDatePicker) {
        val initial = try { LocalDate.parse(periodStart) } catch (e: Exception) { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date }
        VisualDatePicker(
            initialDate = initial,
            onDismissRequest = { showStartDatePicker = false },
            onDateSelected = { date ->
                periodStart = date.toString()
                showStartDatePicker = false
            }
        )
    }

    if (showEndDatePicker) {
        val initial = try { LocalDate.parse(periodEnd) } catch (e: Exception) { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date }
        VisualDatePicker(
            initialDate = initial,
            onDismissRequest = { showEndDatePicker = false },
            onDateSelected = { date ->
                periodEnd = date.toString()
                showEndDatePicker = false
            }
        )
    }
}
